import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
// import { Fade, Zoom } from "react-awesome-reveal";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../cabins.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinus,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip, useMediaQuery } from "@mui/material";
import {
  fetchCruiseExtras,
  removeExtra,
  selectExtra,
} from "../../../data/groupBooking/getExtraSlice";
import { fetchCheckOut } from "../../../data/groupBooking/checkOutSlice";
import Loader from "../../../Loader";
import { BrandColorContext } from "../../../App";
import BookingDetails from "./BookingDetails";
import { useTranslation } from "react-i18next";
import LoaderInternal from "../../../LoaderInternal";

const AddExtra = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(max-width:1024px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const brandColor = useContext(BrandColorContext);
  const queryParams = new URLSearchParams(location.search);
  const { extras, selectedExtras, loadingExtras, error } = useSelector(
    (state) => state.extras
  );
  const language = useSelector((state) => state.language.language) || "en";
  const chainCode = queryParams.get("chainCode");
  const cruiseCode = queryParams.get("cruiseCode");
  const itineraryId = Number(queryParams.get("itineraryId"));
  const operationDate = queryParams.get("operationDate");
  // const numberRooms = queryParams.get("numberRooms");
  // const adultNumber = queryParams.get("adultNumber");
  // const childNumber = queryParams.get("childNumber");
  // const childAgesParam = queryParams.get("childAges");
  // const operationID = queryParams.get("operationID");
  const cruiseId = Number(queryParams.get("cruiseId"));
  // const cbinID = queryParams.get("cbinID");
  // const cruiseRateId = queryParams.get("cruiseRateId");
  const numCabins = queryParams.get("numCabins");
  const [cabinsData, setCabinsData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 5;
  const lastRequestRef = useRef({ search: "", page: 0, cabinsHash: "" });

  // Build cabins data once when component mounts or search params change
  useEffect(() => {
    const cabins = [];
    for (let i = 1; i <= numCabins; i++) {
      const cabinId = parseInt(queryParams.get(`cabinId_room_${i}`), 10) || 1;
      const rateTypeId =
        parseInt(queryParams.get(`rateTypeId_room_${i}`), 10) || 1;
      const adults = parseInt(queryParams.get(`adults_room_${i}`), 10) || 1;
      const children = parseInt(queryParams.get(`children_room_${i}`), 10) || 0;
      const childrenAgesParam = queryParams.get(`childAges_room_${i}`);
      const childrenAges =
        childrenAgesParam &&
        childrenAgesParam !== "undefined" &&
        childrenAgesParam !== "null"
          ? childrenAgesParam.split(",").filter(Boolean).map(Number)
          : [];
      cabins.push({ cabinId, rateTypeId, adults, children, childrenAges });
    }

    setCabinsData(cabins);
    setPageNumber(1);
    lastRequestRef.current = {
      search: location.search,
      page: 1,
      cabinsHash: "",
    };
  }, [location.search, numCabins]);

  // Fetch extras when cabins data or page number changes
  useEffect(() => {
    if (cabinsData.length === 0) return;

    // Create a hash of cabins data to detect changes
    const cabinsHash = JSON.stringify(cabinsData);

    // Prevent duplicate requests
    if (
      lastRequestRef.current.search === location.search &&
      lastRequestRef.current.page === pageNumber &&
      lastRequestRef.current.cabinsHash === cabinsHash
    ) {
      return;
    }

    const payload = {
      cruiseId: cruiseId,
      itineraryId,
      operationDate,
      cabins: cabinsData,
    };

    lastRequestRef.current = {
      search: location.search,
      page: pageNumber,
      cabinsHash,
    };

    dispatch(
      fetchCruiseExtras({ payload, chainCode, pageNumber, pageSize, language })
    ).catch((error) => {
      console.error("Failed to fetch Extras list:", error);
    });
  }, [
    dispatch,
    chainCode,
    cruiseId,
    itineraryId,
    operationDate,
    pageNumber,
    pageSize,
    location.search,
    cabinsData,
  ]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (extras?.pagination?.totalPages || 1)) {
      setPageNumber(newPage);
      // Scroll to top of extras section
      const extrasSection = document.getElementById("cabin-details");
      if (extrasSection) {
        extrasSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // Sync pageNumber with pagination from API
  const currentPage = extras?.pagination?.pageNumber || pageNumber;

  const [selectionPrice, setSelectionPrice] = useState(0);
  const [checkedExtras, setCheckedExtras] = useState({});
  const [extraQuantities, setExtraQuantities] = useState({});
  const handleSelectExtra = (cruiseExtraID, checked) => {
    dispatch(selectExtra({ cruiseExtraID, checked }));
    setCheckedExtras((prev) => ({
      ...prev,
      [cruiseExtraID]: checked,
    }));
    if (checked) {
      setExtraQuantities((prev) => ({
        ...prev,
        [cruiseExtraID]: { adults: 1, children: 0 },
      }));
    } else {
      setExtraQuantities((prev) => {
        const newQuantities = { ...prev };
        delete newQuantities[cruiseExtraID];
        return newQuantities;
      });
    }
  };
  const handleRemoveExtra = (cruiseExtraID) => {
    dispatch(removeExtra(cruiseExtraID));
    setCheckedExtras((prev) => ({
      ...prev,
      [cruiseExtraID]: false,
    }));
    setExtraQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[cruiseExtraID];
      return newQuantities;
    });
  };

  const handleQuantityChange = (extraId, type, value) => {
    setExtraQuantities((prev) => ({
      ...prev,
      [extraId]: {
        ...prev[extraId],
        [type]: parseInt(value, 10),
      },
    }));
  };
  const calculateTotalPrice = useCallback(() => {
    return selectedExtras.reduce((total, extra) => {
      const quantities = extraQuantities[extra.cruiseExtraID] || {
        adults: 0,
        children: 0,
      };
      const extraTotal =
        quantities.adults * extra.cruiseExtraPrice +
        (extra.isChildAvailable
          ? quantities.children * extra.cruiseExtraChildPrice
          : 0);
      return total + extraTotal;
    }, 0);
  }, [selectedExtras, extraQuantities]);

  const finalTotalPrice = useMemo(() => {
    const cabinsTotal = extras?.totalCabins || 0;
    const taxesTotal =
      extras?.cruisesTaxes
        ?.filter((t) => t.isAppliedOnExtras)
        .reduce((acc, tax) => acc + tax.taxValue, 0) || 0;

    const subTotal = cabinsTotal + calculateTotalPrice();
    setSelectionPrice(subTotal);
    const taxAmount = subTotal * (taxesTotal / 100);

    return subTotal + taxAmount;
  }, [extras, calculateTotalPrice]);
  const handleBookingClick = useCallback(async () => {
    if (extras) {
      const bookingPayload = {
        booking: {
          cruiseId: cruiseId,
          itineraryId: itineraryId,
          operationDate: operationDate,
        },
        cabins: cabinsData,
        extras: Object.keys(extraQuantities).map((extraId) => ({
          extraId: parseInt(extraId, 10),
          adultQuantity: extraQuantities[extraId].adults,
          childQuantity: extraQuantities[extraId].children,
        })),
      };

      try {
        const resultAction = await dispatch(
          fetchCheckOut({ payload: bookingPayload, chainCode, language })
        );
        if (fetchCheckOut.fulfilled.match(resultAction)) {
          const params = {
            lang: language,
            chainCode: chainCode,
            cruiseId: cruiseId,
            itineraryId: itineraryId,
            operationDate: operationDate,
            // Add bookingId and bookingRef from the successful response
            // bookingId: resultAction.payload.bookingId,
            // bookingRef: resultAction.payload.bookingRef,
            // Cabins Info
            numCabins: cabinsData.length,
            // Extras Info
            extras: JSON.stringify(bookingPayload.extras),
          };

          cabinsData.forEach((cabin, index) => {
            const roomNum = index + 1;
            params[`cabinId_room_${roomNum}`] = cabin.cabinId;
            params[`rateTypeId_room_${roomNum}`] = cabin.rateTypeId;
            params[`adults_room_${roomNum}`] = cabin.adults;
            params[`children_room_${roomNum}`] = cabin.children;
            params[`childAges_room_${roomNum}`] = cabin.childrenAges.join(",");
          });

          const queryString = new URLSearchParams(params).toString();
          navigate(`/checkout?${queryString}`);
        } else {
          throw new Error(resultAction.error.message);
        }
      } catch (err) {
        alert(err.message || t("common.errorOccurredWhileSearching"));
      }
      // dispatch(fetchCheckOut(payload));
      // navigate("/checkout");
    }
  }, [extras, dispatch, navigate, chainCode, cabinsData, extraQuantities]);
  useEffect(() => {
    if (selectedExtras && selectedExtras.length > 0) {
      // Rebuild checked extras
      const restoredChecked = {};
      const restoredQuantities = {};

      selectedExtras.forEach((extra) => {
        restoredChecked[extra.cruiseExtraID] = true;

        // لو عندك quantities محفوظة في redux حطها
        // لكن واضح من الكود إن redux مش شايل quantities
        // فهستخدم default 1 adult, 0 children
        restoredQuantities[extra.cruiseExtraID] = {
          adults: 1,
          children: extra.isChildAvailable ? 0 : 0,
        };
      });

      setCheckedExtras(restoredChecked);
      setExtraQuantities(restoredQuantities);
    }
  }, [selectedExtras]);

  return (
    <Fragment>
      {error ? (
        <p>{error}</p>
      ) : (
        <Fragment>
          <Helmet>
            <title>{t("booking.addExtra")}</title>
            <style>
              {`
          .header .header-menu .menu li a{
          color:#051036;
          }
          .header .header-menu .menu .subnav>li>a {
          color:#051036 !important;
          }
          .is-sticky.bg-dark-1{
          background-color:#fff !important;
          }
          // .is-sticky .menu li a{
          // color:#fff !important;
          // }
          .ratio-15\:9::before{
          padding-bottom:0 !important;}
          
          .form-checkbox__mark{
            width: 25px !important;
  height: 25px !important;
          }     
          `}
            </style>
          </Helmet>
          {/*banner */}
          <section className="row d-flex items-center  mt-60  ">
            <div className="  mt-40 py-40 bg-light-2 border-top-light">
              <div className="container d-flex justify-content-between m-auto px-1    y-gap-30 items-center ">
                <div className="col-auto pe-1">
                  <Tooltip
                    arrow
                    title={
                      chainCode != "cyanegypt"
                        ? t("booking.chooseCruise")
                        : null
                    }
                  >
                    <div
                      className="d-flex items-center"
                      onClick={() =>
                        chainCode != "cyanegypt" &&
                        navigate(`/booking-cruises${location.search}`)
                      }
                      style={{
                        cursor:
                          chainCode != "cyanegypt" ? "pointer" : "default",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = brandColor;
                      }}
                    >
                      <div className="size-40 rounded-full flex-center bg-blue-1">
                        <i className="icon-check text-16 text-white"></i>
                      </div>
                      <div className="text-18 text-blue-1 fw-500 ml-10">
                        {t("booking.chooseCruise")}
                      </div>
                    </div>
                  </Tooltip>
                </div>{" "}
                <div className="col px-1">
                  <div className="w-full h-1 border-blue-1 bg-border"></div>
                </div>
                <div className="col-auto  px-1">
                  <Tooltip
                    arrow
                    title={
                      chainCode != "cyanegypt" ? t("booking.chooseCabin") : null
                    }
                  >
                    <div
                      className="d-flex items-center"
                      onClick={() =>
                        chainCode != "cyanegypt" &&
                        navigate(`/cruises-details${location.search}`)
                      }
                      style={{
                        cursor:
                          chainCode != "cyanegypt" ? "pointer" : "default",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = brandColor;
                      }}
                    >
                      <div className="size-40 rounded-full flex-center bg-blue-1">
                        <i className="icon-check text-16 text-white"></i>
                      </div>
                      <div className="text-18 text-blue-1 fw-500 ml-10">
                        {t("booking.chooseCabin")}
                      </div>
                    </div>
                  </Tooltip>
                </div>
                <div className="col px-1">
                  <div className="w-full h-1 border-blue-1 bg-border"></div>
                </div>
                <Fragment>
                  <div className="col-auto px-1">
                    <div className="d-flex items-center">
                      <div className="size-40 rounded-full flex-center bg-blue-2">
                        <div className="size-40 rounded-full flex-center bg-blue-1">
                          <span className=" text-16 text-white">3</span>
                        </div>
                      </div>
                      <div className="text-18 fw-500 ml-10">
                        {t("booking.addExtra")}
                      </div>
                    </div>
                  </div>

                  <div className="col px-1">
                    <div className="w-full h-1 bg-border"></div>
                  </div>
                </Fragment>
                <div className="col-auto px-1">
                  <div className="d-flex items-center">
                    <div className="size-40 rounded-full flex-center bg-blue-1-05 text-blue-1 fw-500">
                      4
                    </div>
                    <div className="text-18 fw-500 ml-10">
                      {t("booking.checkout")}{" "}
                    </div>
                  </div>
                </div>
                <div className="col px-1">
                  <div className="w-full h-1 bg-border"></div>
                </div>
                <div className="col-auto px-1">
                  <div className="d-flex items-center">
                    <div className="size-40 rounded-full flex-center bg-blue-1-05 text-blue-1 fw-500">
                      5
                    </div>
                    <div className="text-18 fw-500 ml-10">
                      {t("booking.bookingCompleted")}{" "}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* form */}
          <section
            className="card-area section--padding my-5"
            id="cabin-details"
          >
            <div className="container">
              <div className="d-flex flex-wrap justify-content-between col-12  mx-auto">
                <div className="col-12 col-lg-7 me-lg-5">
                  <h3 className="mb-3">{t("booking.selectExtras")}</h3>
                  {loadingExtras ? (
                    <LoaderInternal
                      text={t("common.loadingAllAvailableExtras")}
                    />
                  ) : extras?.extras?.length ? (
                    <div className="col-xs-12 fb-results-accommodation  mb-5 ">
                      <div className="col-xs-12 d-flex justify-content-between align-items-center fb-dark-bg fb-block-header fb-results-acc-title mt-0 ">
                        <span>
                          {extras.totalAvailableExtras}{" "}
                          {extras.totalAvailableExtras > 1
                            ? t("booking.extrasAvailable")
                            : t("booking.extraAvailable")}
                        </span>
                      </div>

                      {extras?.extras?.map((extra) => (
                        <label
                          htmlFor={`select-${extra.cruiseExtraID}`}
                          style={{
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            position: "relative",
                          }}
                          key={extra.cruiseExtraID}
                          className="d-flex justify-content-between align-items-start px-3 py-10"
                        >
                          {" "}
                          <div className="col-12 col-lg-3">
                            <img
                              src={extra.cruiseExtraPhoto}
                              alt={extra.cruiseExtraNameSys}
                              loading="lazy"
                              className="me-2"
                              style={{ width: "100%", borderRadius: "10px" }}
                            />
                          </div>
                          <div className="room-content text-left me-auto border-0 pt-0 col-8 col-lg-7 my-auto">
                            <h5 className="fs-4 mt-0 pt-0">
                              {extra.cruiseExtraNameSys}
                            </h5>

                            <div className="room-amenities-item">
                              <ul className="col-12 d-flex justify-content-between flex-wrap">
                                <p
                                  className="my-2"
                                  dangerouslySetInnerHTML={{
                                    __html: extra.cruiseExtraDescription,
                                  }}
                                />
                              </ul>
                            </div>
                          </div>
                          <div className="   d-flex align-items-center justify-content-between position-relative">
                            <div
                              className="card-btn "
                              style={{ bottom: "unset" }}
                            >
                              <div className="form-checkbox d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  name="name"
                                  id={`select-${extra.cruiseExtraID}`}
                                  checked={
                                    checkedExtras[extra.cruiseExtraID] || false
                                  }
                                  onChange={(e) =>
                                    handleSelectExtra(
                                      extra.cruiseExtraID,
                                      e.target.checked
                                    )
                                  }
                                />
                                <div className="form-checkbox__mark">
                                  <div className="form-checkbox__icon icon-check"></div>
                                </div>
                                <div className="col-auto ms-2">
                                  <label
                                    htmlFor={`select-${extra.cruiseExtraID}`}
                                    className="text-16 text-light-1"
                                  >
                                    {t("common.select")}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <span
                            to="#."
                            style={{
                              color: "var(--color-brand)",
                              position: "absolute",
                              bottom: "4vh",
                              right: "0.5vw",
                              textAlign: "right",
                            }}
                          >
                            {" "}
                            <b>
                              {extras.currencyCode}
                              {extra.cruiseExtraPrice}
                            </b>{" "}
                            <span
                              className="me-2 text-secondary fw-bold col-3  "
                              style={{
                                fontSize: "14px",
                              }}
                            >
                              {t("common.perAdult")}
                            </span>
                            {extras?.cabinsChoices.reduce(
                              (total, cabin) => total + cabin.childrenNum,
                              0
                            ) > 0 && (
                              <>
                                <br />
                                <b>
                                  {extra.isChildAvailable
                                    ? extras.currencyCode
                                    : ""}
                                  {extra.isChildAvailable
                                    ? extra.cruiseExtraChildPrice
                                    : t("common.notAvailable")}
                                </b>
                                <span
                                  className="me-2 text-secondary fw-bold col-3  "
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  {t("common.perChild")}
                                </span>
                              </>
                            )}
                          </span>
                        </label>
                      ))}

                      {/* Pagination Controls */}
                      {extras?.pagination &&
                        extras.pagination.totalPages > 1 && (
                          <article
                            style={{ width: "100%" }}
                            className="w-100 d-flex flex-column justify-content-center align-items-center"
                          >
                            <div
                              className="d-flex justify-content-between w-100 align-items-center mt-4 mb-3"
                              style={{
                                width: "100%",
                                padding: "15px",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: `1px solid ${brandColor}20`,
                              }}
                            >
                              {" "}
                              <div
                                style={{ width: "100%" }}
                                className="d-flex align-items-center justify-content-between w-100 g-5 gap-5"
                              >
                                <button
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                  disabled={currentPage === 1 || loadingExtras}
                                  style={{
                                    padding: "8px",
                                    width: "40px",
                                    height: "40px",
                                    border: `1px solid ${brandColor}`,
                                    background:
                                      currentPage === 1 ? "#f0f0f0" : "white",
                                    color:
                                      currentPage === 1 ? "#999" : brandColor,
                                    borderRadius: "50%",
                                    cursor:
                                      currentPage === 1
                                        ? "not-allowed"
                                        : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    // gap: "16px",
                                    transition: "all 0.3s ease",
                                    opacity: currentPage === 1 ? 0.5 : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (currentPage !== 1) {
                                      e.target.style.background = `${brandColor}10`;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (currentPage !== 1) {
                                      e.target.style.background = "white";
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faChevronLeft} />
                                  {/* <span>{t("common.previous")}</span> */}
                                </button>

                                <div
                                  className="d-flex align-items-center gap-4 g-4"
                                  style={{ gap: "8px" }}
                                >
                                  {Array.from(
                                    { length: extras.pagination.totalPages },
                                    (_, i) => i + 1
                                  )
                                    .filter((page) => {
                                      // Show first page, last page, current page, and pages around current
                                      if (
                                        page === 1 ||
                                        page === extras.pagination.totalPages
                                      )
                                        return true;
                                      if (Math.abs(page - currentPage) <= 1)
                                        return true;
                                      return false;
                                    })
                                    .map((page, index, array) => {
                                      // Add ellipsis if there's a gap
                                      const showEllipsisBefore =
                                        index > 0 &&
                                        page - array[index - 1] > 1;

                                      return (
                                        <React.Fragment key={page}>
                                          {showEllipsisBefore && (
                                            <span
                                              className="px-2"
                                              style={{ color: "#666" }}
                                            >
                                              ...
                                            </span>
                                          )}
                                          <button
                                            onClick={() =>
                                              handlePageChange(page)
                                            }
                                            disabled={loadingExtras}
                                            style={{
                                              minWidth: "40px",
                                              height: "40px",
                                              padding: "8px",
                                              border: `1px solid ${
                                                page === currentPage
                                                  ? brandColor
                                                  : "#ddd"
                                              }`,
                                              background:
                                                page === currentPage
                                                  ? brandColor
                                                  : "white",
                                              color:
                                                page === currentPage
                                                  ? "white"
                                                  : "#333",
                                              borderRadius: "50%",
                                              cursor: "pointer",
                                              fontWeight:
                                                page === currentPage
                                                  ? "600"
                                                  : "400",
                                              transition: "all 0.3s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                              if (page !== currentPage) {
                                                e.target.style.background = `${brandColor}10`;
                                                e.target.style.borderColor =
                                                  brandColor;
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (page !== currentPage) {
                                                e.target.style.background =
                                                  "white";
                                                e.target.style.borderColor =
                                                  "#ddd";
                                              }
                                            }}
                                          >
                                            {page}
                                          </button>
                                        </React.Fragment>
                                      );
                                    })}
                                </div>

                                <button
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                  disabled={
                                    currentPage >=
                                      extras.pagination.totalPages ||
                                    loadingExtras
                                  }
                                  style={{
                                    padding: "8px",
                                    width: "40px",
                                    height: "40px",
                                    border: `1px solid ${brandColor}`,
                                    background:
                                      currentPage >=
                                      extras.pagination.totalPages
                                        ? "#f0f0f0"
                                        : "white",
                                    color:
                                      currentPage >=
                                      extras.pagination.totalPages
                                        ? "#999"
                                        : brandColor,
                                    borderRadius: "50%",
                                    cursor:
                                      currentPage >=
                                      extras.pagination.totalPages
                                        ? "not-allowed"
                                        : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    // gap: "8px",
                                    transition: "all 0.3s ease",
                                    opacity:
                                      currentPage >=
                                      extras.pagination.totalPages
                                        ? 0.5
                                        : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (
                                      currentPage < extras.pagination.totalPages
                                    ) {
                                      e.target.style.background = `${brandColor}10`;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (
                                      currentPage < extras.pagination.totalPages
                                    ) {
                                      e.target.style.background = "white";
                                    }
                                  }}
                                >
                                  {/* <span>{t("common.next")}</span> */}
                                  <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-14 text-light-1">
                                {t("common.showing")}{" "}
                                {(extras.pagination.pageNumber - 1) *
                                  extras.pagination.pageSize +
                                  1}{" "}
                                -{" "}
                                {Math.min(
                                  extras.pagination.pageNumber *
                                    extras.pagination.pageSize,
                                  extras.pagination.totalRecords
                                )}{" "}
                                {t("common.of")}{" "}
                                {extras.pagination.totalRecords}{" "}
                                {t("common.extras")}
                              </span>
                            </div>
                          </article>
                        )}
                    </div>
                  ) : (
                    <p className="text-danger">{t("booking.noExtras")}</p>
                  )}
                </div>
                {/* Bookind Info  */}
                <div className="col-12 col-lg-4">
                  <div className="">
                    {/* Extras  */}
                    {selectedExtras?.length > 0 && (
                      <div className="form-box booking-detail-form mt-20  px-30 pt-30 border-light rounded-4">
                        <div className="form-content">
                          <div className="card-item shadow-none radius-none mb-0">
                            <div className="card-body p-0">
                              <ul className="list-items list-items-2 py-0">
                                <li className="d-flex justify-content-between align-items-center mb-1">
                                  <h6 className="me-2  fw-bold  ">
                                    {t("booking.extras")}
                                  </h6>
                                  <h6 className="me-2 fw-bold text-primary">
                                    {extras.currencyCode}
                                    {calculateTotalPrice()}
                                  </h6>
                                </li>
                                <div className="w-full h-1 bg-border my-2 "></div>
                                {selectedExtras?.map((extra, index) => (
                                  <li
                                    key={extra.cruiseExtraID}
                                    className={`row d-flex justify-content-between align-items-center py-2 ${
                                      index % 2 === 1 && "bg-light"
                                    }`}
                                    style={{
                                      borderBottom:
                                        selectedExtras.length - 1 > index &&
                                        "1px solid #ccc",
                                    }}
                                  >
                                    <span className="text-center fw-bold">
                                      {" "}
                                      {extra.cruiseExtraNameSys} (
                                      <span className="me-0 fw-bold col-lg-3 text-center text-12  text-primary">
                                        {extras.currencyCode}{" "}
                                        {(
                                          (extraQuantities[extra.cruiseExtraID]
                                            ?.adults || 0) *
                                            extra.cruiseExtraPrice +
                                          (extra.isChildAvailable
                                            ? (extraQuantities[
                                                extra.cruiseExtraID
                                              ]?.children || 0) *
                                              extra.cruiseExtraChildPrice
                                            : 0)
                                        ).toFixed(2)}
                                      </span>
                                      )
                                    </span>
                                    <span className="d-flex align-items-center me-1 col-lg-11">
                                      {/* <FontAwesomeIcon
                                        icon={faSquarePlus}
                                        className="text-13 text-main pe-1"
                                      /> */}
                                      <img
                                        src={extra.cruiseExtraPhoto}
                                        width={60}
                                        height={60}
                                        alt={extra.cruiseExtraNameSys}
                                        className="rounded-full px-1"
                                      />
                                      <select
                                        className="form-select w-auto text-13 fw-500 text-center"
                                        value={
                                          extraQuantities[extra.cruiseExtraID]
                                            ?.adults || 1
                                        }
                                        onChange={(e) =>
                                          handleQuantityChange(
                                            extra.cruiseExtraID,
                                            "adults",
                                            e.target.value
                                          )
                                        }
                                      >
                                        {[...Array(10)].map((_, i) => (
                                          <option
                                            className="form-select text-14"
                                            key={i + 1}
                                            value={i + 1}
                                          >
                                            {i + 1}{" "}
                                            {i + 1 > 1
                                              ? t("common.extras")
                                              : t("common.extra")}{" "}
                                            {t("common.forAdults")}
                                          </option>
                                        ))}
                                      </select>
                                      {extra.isChildAvailable &&
                                        extras?.cabinsChoices.reduce(
                                          (total, cabin) =>
                                            total + cabin.childrenNum,
                                          0
                                        ) > 0 && (
                                          <select
                                            className="form-select w-auto text-13 fw-500 text-center"
                                            value={
                                              extraQuantities[
                                                extra.cruiseExtraID
                                              ]?.children || 0
                                            }
                                            onChange={(e) =>
                                              handleQuantityChange(
                                                extra.cruiseExtraID,
                                                "children",
                                                e.target.value
                                              )
                                            }
                                          >
                                            {[...Array(10)].map((_, i) => (
                                              <option
                                                className="form-select text-14"
                                                key={i}
                                                value={i}
                                              >
                                                {i}{" "}
                                                {i > 1
                                                  ? t("common.extras")
                                                  : t("common.extra")}{" "}
                                                {t("common.forChild")}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                    </span>

                                    <button
                                      className="py-0 px-0 col-lg-1 text-light bg-danger"
                                      style={{
                                        display: "inline-block",

                                        float: "right",
                                        width: "20px",
                                        height: "20px",
                                        lineHeight: "1",
                                        borderRadius: "50%",
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                      }}
                                      onClick={() =>
                                        handleRemoveExtra(extra.cruiseExtraID)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faMinus} />{" "}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <BookingDetails data={extras} />

                    <div className="px-30 py-30 border-light rounded-4 mt-30">
                      <div className="text-20 fw-500 mb-20">
                        {t("booking.yourSelection")}{" "}
                      </div>
                      <div className="row y-gap-5 justify-between">
                        <div className="col-auto">
                          <div className="text-15">{t("booking.cabins")}</div>
                        </div>
                        <div className="col-auto">
                          <div className="text-15">
                            {extras.currencyCode}
                            {extras.totalCabins}
                          </div>
                        </div>
                      </div>
                      <div className="row y-gap-5 justify-between">
                        <div className="col-auto">
                          <div className="text-15">{t("booking.extras")}</div>
                        </div>
                        <div className="col-auto">
                          <div className="text-15">
                            {extras.currencyCode}
                            {calculateTotalPrice()}
                          </div>
                        </div>
                      </div>
                      {/* {extras?.cruisesTaxes?.map((item, index) => (
                        <div
                          key={item.taxID}
                          className="row y-gap-5 justify-between"
                        >
                          <div className="col-auto">
                            <div className="text-15">{item.taxName} </div>
                          </div>
                          <div className="col-auto">
                            <div className="text-15">
                               %{item.taxValue}{" "}
                            </div>
                          </div>
                        </div>
                      ))} */}

                      <div className="px-20 py-20 bg-blue-2 rounded-4 mt-20">
                        <div className="row y-gap-5 justify-between">
                          <div className="col-auto">
                            <div className="text-18 lh-13 fw-500">
                              {t("booking.price")}{" "}
                            </div>
                          </div>
                          <div className="col-auto">
                            <div className="text-18 lh-13 fw-500">
                              {extras.currencyCode}
                              {selectionPrice.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>{" "}
                  <div
                    className="row d-block mx-auto    mt-3 text-center"
                    style={{ bottom: "unset" }}
                  >
                    <button
                      type="button"
                      onClick={handleBookingClick}
                      className="button h-60 px-24 -dark-1 bg-blue-1 text-white  text-center mx-auto"
                    >
                      {t("booking.bookNow")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Fragment>
      )}
    </Fragment>
  );
};

export default AddExtra;
